import React, { useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { Plus, Search, Edit2, AlertTriangle, Shield, Package, ChevronDown, X, Check, Printer, GitMerge } from 'lucide-react';
import api from '../../services/api';
import { fmt } from './insumos.utils';

function AlertaConfigModal({ material, onClose }) {
  const qc = useQueryClient();
  const [estoque_minimo, setMin] = useState(material.estoque_minimo || 0);
  const [critico, setCritico] = useState(material.critico || false);

  const save = useMutation({
    mutationFn: () => api.post('/insumos/alertas/config', { material_id: material.id, estoque_minimo, critico }),
    onSuccess: () => { qc.invalidateQueries(['insumos-materiais']); onClose(); },
  });

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-800">Alerta de Estoque</h3>
          <button onClick={onClose}><X size={18} className="text-gray-400" /></button>
        </div>
        <p className="text-sm text-gray-600 mb-4">{material.nome}</p>
        <label className="block text-xs text-gray-500 mb-1">Quantidade mínima ({material.unidade_estoque_sigla || 'un'})</label>
        <input type="number" min="0" step="0.01" value={estoque_minimo}
          onChange={e => setMin(e.target.value)}
          className="w-full border rounded-lg px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-400" />
        <label className="flex items-center gap-2 mb-4 cursor-pointer">
          <input type="checkbox" checked={critico} onChange={e => setCritico(e.target.checked)} className="w-4 h-4" />
          <span className="text-sm text-gray-700">Marcar como material crítico</span>
        </label>
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 px-4 py-2 border rounded-lg text-sm text-gray-600 hover:bg-gray-50">Cancelar</button>
          <button onClick={() => save.mutate()} disabled={save.isPending}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
            {save.isPending ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  );
}

function MaterialFormModal({ material, categorias, unidades, onClose }) {
  const qc = useQueryClient();
  const isEdit = !!material?.id;
  const [form, setForm] = useState({
    nome: material?.nome || '',
    descricao: material?.descricao || '',
    categoria_id: material?.categoria_id || '',
    unidade_estoque_id: material?.unidade_estoque_id || '',
    unidade_compra_id: material?.unidade_compra_id || '',
    fator_conversao: material?.fator_conversao || 1,
    preco_ref: material?.preco_ref || '',
    barcode: material?.barcode || '',
    estoque_minimo: material?.estoque_minimo || 0,
    critico: material?.critico || false,
  });
  const [erro, setErro] = useState('');

  const save = useMutation({
    mutationFn: () => isEdit
      ? api.put(`/insumos/materiais/${material.id}`, form)
      : api.post('/insumos/materiais', form),
    onSuccess: () => { qc.invalidateQueries(['insumos-materiais']); onClose(); },
    onError: (e) => setErro(e.response?.data?.error || 'Erro ao salvar'),
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-semibold text-gray-800 text-lg">{isEdit ? 'Editar Material' : 'Novo Material'}</h3>
          <button onClick={onClose}><X size={18} className="text-gray-400" /></button>
        </div>
        {erro && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">{erro}</div>}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-xs text-gray-500 mb-1">Nome *</label>
            <input value={form.nome} onChange={e => set('nome', e.target.value)}
              className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Categoria</label>
            <select value={form.categoria_id} onChange={e => set('categoria_id', e.target.value)}
              className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400">
              <option value="">Selecionar...</option>
              {categorias.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Barcode / Código de Varredura</label>
            <input value={form.barcode} onChange={e => set('barcode', e.target.value)}
              className="w-full border rounded-lg px-3 py-2 font-mono focus:outline-none focus:ring-2 focus:ring-blue-400" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Unidade de Estoque (controle)</label>
            <select value={form.unidade_estoque_id} onChange={e => set('unidade_estoque_id', e.target.value)}
              className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400">
              <option value="">Selecionar...</option>
              {unidades.map(u => <option key={u.id} value={u.id}>{u.nome} ({u.sigla})</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Unidade de Compra (NF/Pedido)</label>
            <select value={form.unidade_compra_id} onChange={e => set('unidade_compra_id', e.target.value)}
              className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400">
              <option value="">Igual à de estoque</option>
              {unidades.map(u => <option key={u.id} value={u.id}>{u.nome} ({u.sigla})</option>)}
            </select>
          </div>
          {form.unidade_compra_id && form.unidade_compra_id !== form.unidade_estoque_id && (
            <div>
              <label className="block text-xs text-gray-500 mb-1">Fator de Conversão (compra → estoque)</label>
              <input type="number" min="0.0001" step="0.0001" value={form.fator_conversao}
                onChange={e => set('fator_conversao', e.target.value)}
                className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400" />
              <p className="text-xs text-gray-400 mt-1">Ex: 1 Caixa = 12 Unidades → fator = 12</p>
            </div>
          )}
          <div>
            <label className="block text-xs text-gray-500 mb-1">Preço de Referência (R$)</label>
            <input type="number" min="0" step="0.01" value={form.preco_ref}
              onChange={e => set('preco_ref', e.target.value)}
              className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Estoque Mínimo Global</label>
            <input type="number" min="0" step="0.01" value={form.estoque_minimo}
              onChange={e => set('estoque_minimo', e.target.value)}
              className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400" />
          </div>
          <div className="flex items-center gap-2 mt-2">
            <input type="checkbox" id="critico" checked={form.critico} onChange={e => set('critico', e.target.checked)} className="w-4 h-4" />
            <label htmlFor="critico" className="text-sm text-gray-700 cursor-pointer">Material crítico (alerta prioritário)</label>
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs text-gray-500 mb-1">Descrição</label>
            <textarea value={form.descricao} onChange={e => set('descricao', e.target.value)} rows={2}
              className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400" />
          </div>
        </div>

        <div className="flex gap-2 mt-6">
          <button onClick={onClose} className="flex-1 px-4 py-2 border rounded-lg text-sm text-gray-600 hover:bg-gray-50">Cancelar</button>
          <button onClick={() => save.mutate()} disabled={save.isPending || !form.nome}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
            {save.isPending ? 'Salvando...' : 'Salvar Material'}
          </button>
        </div>
      </div>
    </div>
  );
}

function MesclarModal({ materiais, onClose, onMesclado }) {
  const qc = useQueryClient();
  const [destinoId, setDestinoId] = useState(materiais[0]?.id || null);
  const [erro, setErro] = useState('');

  const mesclar = useMutation({
    mutationFn: () => api.post('/insumos/materiais/mesclar', {
      destino_id: destinoId,
      origem_ids: materiais.filter(m => m.id !== destinoId).map(m => m.id),
    }),
    onSuccess: () => { qc.invalidateQueries(['insumos-materiais']); onMesclado(); },
    onError: (e) => setErro(e.response?.data?.error || 'Erro ao mesclar materiais'),
  });

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-lg">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-800 flex items-center gap-2"><GitMerge size={18} className="text-purple-600" /> Mesclar Materiais</h3>
          <button onClick={onClose}><X size={18} className="text-gray-400" /></button>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Escolha qual material será o <strong>principal</strong>. Os demais serão desativados e todo
          o saldo, histórico de entradas/saídas e códigos de fornecedor passam para ele.
        </p>
        <div className="space-y-2 mb-4 max-h-64 overflow-y-auto">
          {materiais.map(m => (
            <label key={m.id} className={`flex items-center gap-3 p-3 border rounded-xl cursor-pointer transition-colors ${destinoId === m.id ? 'border-purple-400 bg-purple-50' : 'hover:bg-gray-50'}`}>
              <input type="radio" name="destino" checked={destinoId === m.id} onChange={() => setDestinoId(m.id)} className="w-4 h-4" />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-800 truncate">{m.nome}</p>
                <p className="text-xs text-gray-400 font-mono">{m.codigo} · saldo: {fmt(m.saldo_total, 2)} {m.unidade_estoque_sigla || ''}</p>
              </div>
              {destinoId === m.id && <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 font-medium shrink-0">Principal</span>}
            </label>
          ))}
        </div>
        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-xl text-xs text-yellow-800 mb-4 flex items-start gap-2">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          Esta ação não pode ser desfeita. NFs futuras desses fornecedores serão vinculadas automaticamente ao material principal.
        </div>
        {erro && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">{erro}</div>}
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 px-4 py-2 border rounded-lg text-sm text-gray-600 hover:bg-gray-50">Cancelar</button>
          <button onClick={() => mesclar.mutate()} disabled={mesclar.isPending || !destinoId}
            className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-50">
            {mesclar.isPending ? 'Mesclando...' : `Mesclar ${materiais.length} materiais`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function InsumosMateriais() {
  const qc = useQueryClient();
  const [busca, setBusca] = useState('');
  const [categoriaFiltro, setCategoriaFiltro] = useState('');
  const [modalForm, setModalForm] = useState(null);
  const [modalAlerta, setModalAlerta] = useState(null);
  const [selecionados, setSelecionados] = useState([]);
  const [gerandoEtiquetas, setGerandoEtiquetas] = useState(false);
  const [modalMesclar, setModalMesclar] = useState(false);

  const { data: matData } = useQuery({
    queryKey: ['insumos-materiais', busca, categoriaFiltro],
    queryFn: () => api.get('/insumos/materiais', { params: { busca, categoria_id: categoriaFiltro, limit: 100 } }).then(r => r.data),
  });
  const { data: cats } = useQuery({ queryKey: ['insumos-cats'], queryFn: () => api.get('/insumos/categorias').then(r => r.data.data) });
  const { data: units } = useQuery({ queryKey: ['insumos-units'], queryFn: () => api.get('/insumos/unidades').then(r => r.data.data) });

  const materiais = matData?.data || [];

  async function gerarEtiquetas() {
    if (!selecionados.length) return;
    setGerandoEtiquetas(true);
    try {
      const resp = await api.post('/insumos/etiquetas/pdf', { material_ids: selecionados }, { responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      window.open(url, '_blank');
    } finally { setGerandoEtiquetas(false); }
  }

  const toggleSel = (id) => setSelecionados(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  return (
    <div className="p-6 space-y-5 max-w-7xl mx-auto">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Package size={22} className="text-blue-600" />
          <h1 className="text-xl font-bold text-gray-800">Catálogo de Materiais</h1>
        </div>
        <div className="flex gap-2">
          {selecionados.length >= 2 && (
            <button onClick={() => setModalMesclar(true)}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-xl text-sm font-medium hover:bg-purple-700">
              <GitMerge size={16} /> Mesclar ({selecionados.length})
            </button>
          )}
          {selecionados.length > 0 && (
            <button onClick={gerarEtiquetas} disabled={gerandoEtiquetas}
              className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-xl text-sm font-medium hover:bg-orange-600 disabled:opacity-50">
              <Printer size={16} /> Etiquetas ({selecionados.length})
            </button>
          )}
          <button onClick={() => setModalForm({})}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700">
            <Plus size={16} /> Novo Material
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input placeholder="Buscar por nome, código, barcode..." value={busca} onChange={e => setBusca(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
        </div>
        <select value={categoriaFiltro} onChange={e => setCategoriaFiltro(e.target.value)}
          className="border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
          <option value="">Todas as categorias</option>
          {(cats || []).map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
        </select>
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-xl shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs text-gray-500 border-b">
            <tr>
              <th className="px-4 py-3 text-left w-8">
                <input type="checkbox" onChange={e => setSelecionados(e.target.checked ? materiais.map(m => m.id) : [])}
                  checked={selecionados.length === materiais.length && materiais.length > 0} className="w-4 h-4" />
              </th>
              <th className="px-4 py-3 text-left">Código</th>
              <th className="px-4 py-3 text-left">Nome</th>
              <th className="px-4 py-3 text-left">Categoria</th>
              <th className="px-4 py-3 text-right">Saldo Total</th>
              <th className="px-4 py-3 text-right">Mín. Global</th>
              <th className="px-4 py-3 text-center">Status</th>
              <th className="px-4 py-3 text-center">Ações</th>
            </tr>
          </thead>
          <tbody>
            {materiais.length ? materiais.map(m => {
              const abaixo = parseFloat(m.saldo_total || 0) < parseFloat(m.estoque_minimo || 0) && parseFloat(m.estoque_minimo || 0) > 0;
              return (
                <tr key={m.id} className={`border-b border-gray-50 hover:bg-gray-50 ${abaixo ? 'bg-red-50' : ''}`}>
                  <td className="px-4 py-3">
                    <input type="checkbox" checked={selecionados.includes(m.id)} onChange={() => toggleSel(m.id)} className="w-4 h-4" />
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-600">{m.codigo}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {m.critico && <Shield size={13} className="text-red-500 shrink-0" title="Crítico" />}
                      <span className="font-medium text-gray-800">{m.nome}</span>
                    </div>
                    <span className="text-xs text-gray-400">{m.barcode}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: (m.categoria_cor || '#e5e7eb') + '33', color: m.categoria_cor || '#374151' }}>
                      {m.categoria_nome || '—'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right font-mono">
                    <span className={abaixo ? 'text-red-600 font-bold' : 'text-gray-700'}>
                      {fmt(m.saldo_total, 2)} {m.unidade_estoque_sigla || ''}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right text-gray-500 font-mono text-xs">{fmt(m.estoque_minimo, 2)}</td>
                  <td className="px-4 py-3 text-center">
                    {abaixo ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-700">
                        <AlertTriangle size={10} /> Abaixo
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700">
                        <Check size={10} /> OK
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <button onClick={() => setModalAlerta(m)} title="Configurar alerta"
                        className="p-1.5 rounded-lg hover:bg-yellow-50 text-yellow-500">
                        <AlertTriangle size={15} />
                      </button>
                      <button onClick={() => setModalForm(m)} title="Editar material"
                        className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-500">
                        <Edit2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            }) : (
              <tr><td colSpan={8} className="text-center py-12 text-gray-400">Nenhum material cadastrado. <button onClick={() => setModalForm({})} className="text-blue-600 underline">Cadastre o primeiro</button>.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {modalMesclar && (
        <MesclarModal
          materiais={materiais.filter(m => selecionados.includes(m.id))}
          onClose={() => setModalMesclar(false)}
          onMesclado={() => { setModalMesclar(false); setSelecionados([]); }}
        />
      )}
      {modalAlerta && <AlertaConfigModal material={modalAlerta} onClose={() => setModalAlerta(null)} />}
      {modalForm !== null && (
        <MaterialFormModal
          material={modalForm}
          categorias={cats || []}
          unidades={units || []}
          onClose={() => setModalForm(null)}
        />
      )}
    </div>
  );
}
